import React, { useState, useMemo, useEffect } from 'react'
import { useGoogleTranslate } from '../../../Hooks/useGoogleTranslate'
import { Col, Container, Row, Button, Spinner, Alert, Dropdown } from 'react-bootstrap'
import { useSearchUsers, useLikeUser, usePassUser, useFavoriteUser } from '../../../Hooks/useMatching'
import BasicFilters from '../../../Components/BasicFilters/BasicFilters'
import './InteractWithHappiness.css'
import verifiedIcon from '../../../assets/images/verified-badge.png';
import { FaCheck, FaStar, FaTimes, FaUserAlt } from 'react-icons/fa'
import { HiAdjustmentsHorizontal } from 'react-icons/hi2'
import { useNavigate } from 'react-router-dom';
import useUserStore from '../../../Stores/UserStore';
import { showToast } from '../../../Components/Toast/toast';
import PremiumBadge from '../../../Components/PremiumBadge/PremiumBadge';
import { getSubscriptionLogs } from '../../../Services/User/SubscriptionPlans';

const InteractWithHappiness = () => {
    const pageTitle = useGoogleTranslate('Real people. Real matches. No endless swiping');
    const loadingText = useGoogleTranslate('Finding your perfect matches...');
    const oopsHeading = useGoogleTranslate('Oops! Something went wrong');
    const noProfilesHeading = useGoogleTranslate('No more profiles to show!');
    const noProfilesText = useGoogleTranslate('Check back later for new matches or adjust your preferences.');
    const tryAgainBtn = useGoogleTranslate('Try Again');
    const refreshBtn = useGoogleTranslate('Refresh');
    const kmAwayLabel = useGoogleTranslate('km away');
    const milesAwayLabel = useGoogleTranslate('miles away');
    const filtersLabel = useGoogleTranslate('Search filters');
    const [currentUserIndex, setCurrentUserIndex] = useState(0)
    const [searchFilters, setSearchFilters] = useState({})
    const [subscriptionDetail, setSubscriptionDetail] = useState(null)
    const [distanceUnit, setDistanceUnit] = useState('mi')
    const navigate = useNavigate();
    const isFullAccess = useUserStore(state => state.hasFullAccess());
    const canFavorite = useUserStore(state => state.canFavorite());

    // Fetch subscription logs when page opens
    useEffect(() => {
        const fetchSubscriptionLogs = async () => {
            try {
                const response = await getSubscriptionLogs();
                setSubscriptionDetail(response);
            } catch (error) {
                console.error('Error fetching subscription logs:', error);
                setSubscriptionDetail(null);
            }
        };

        fetchSubscriptionLogs();
    }, []);
    // Fetch users using react-query
    const {
        data: usersData = [],
        isLoading,
        error,
        refetch
    } = useSearchUsers(searchFilters)

    // Sort users so Premium users appear at the top
    const users = useMemo(() => {
        if (!usersData || usersData.length === 0) return [];

        return [...usersData].sort((a, b) => {
            // Check if user is premium
            const isAPremium = () => {
                const planName = a.subscription_plan_name?.toLowerCase() || '';
                const subscriptionType = a.subscription_type?.toLowerCase() || '';
                return planName.includes('premium') || subscriptionType === 'premium' || (a.is_subscribed && planName.includes('premium'));
            };

            const isBPremium = () => {
                const planName = b.subscription_plan_name?.toLowerCase() || '';
                const subscriptionType = b.subscription_type?.toLowerCase() || '';
                return planName.includes('premium') || subscriptionType === 'premium' || (b.is_subscribed && planName.includes('premium'));
            };

            // Premium users first
            if (isAPremium() && !isBPremium()) return -1;
            if (!isAPremium() && isBPremium()) return 1;
            return 0; // Keep original order for same tier
        });
    }, [usersData]);

    // Mutations for like, pass, and favorite actions
    const likeMutation = useLikeUser()
    const passMutation = usePassUser()
    const favoriteMutation = useFavoriteUser()

    const currentUser = users[currentUserIndex]

    const distanceKm = currentUser?.preferences?.distance_max_km ?? 30
    const distanceMiles = Math.round(distanceKm * 0.621371)

    console.log(currentUser, 'currentUserssssssssssssssss')

    const handleLike = async () => {
        if (!currentUser) return

        try {
            await likeMutation.mutateAsync(currentUser.id)
            // Move to next user after successful like
            setCurrentUserIndex(prev => prev + 1)
        } catch (error) {
            console.error('Failed to like user:', error)
        }
    }

    const handlePass = async () => {
        if (!currentUser) return

        try {
            await passMutation.mutateAsync(currentUser.id)
            // Move to next user after successful pass
            setCurrentUserIndex(prev => prev + 1)
        } catch (error) {
            console.error('Failed to pass user:', error)
        }
    }

    const handleFavorite = async () => {
        if (!currentUser) return;

        // Check if user has active subscription from /user/subscription/logs
        if (!subscriptionDetail?.current_subscription) {
            showToast('Please subscribe to a plan to favorite profiles', 'error');
            navigate('/subscription-plans');
            return;
        }

        try {
            console.log('Favoriting user:', currentUser.id);
            const result = await favoriteMutation.mutateAsync(currentUser.id);
            console.log('Favorite result:', result);
            showToast('Profile added to favorites!', 'success');
            // Move to next user after successful favorite
            setCurrentUserIndex(prev => prev + 1);
        } catch (error) {
            console.error('Failed to favorite user:', error);
            // If error is about membership, show upgrade message
            if (error?.message?.toLowerCase().includes('upgrade') ||
                error?.message?.toLowerCase().includes('membership') ||
                error?.message?.toLowerCase().includes('subscription')) {
                showToast('Please upgrade to Standard or Premium membership to favorite profiles', 'error');
                navigate('/subscription-plans');
            }
            // Error toast is handled by the mutation hook
        }
    }

    const handleRefresh = () => {
        setCurrentUserIndex(0)
        refetch()
    }

    const handleApplyFilters = (filters) => {
        setSearchFilters(filters)
        setCurrentUserIndex(0)
        // The useSearchUsers hook will automatically refetch with new filters
    }

    const handleClearFilters = () => {
        setSearchFilters({})
        setCurrentUserIndex(0)
    }

    if (isLoading) {
        return (
            <main className="encounters-page">
                <section className="encounters-hero">
                    <div className="appContainer">
                        <h1>Interact With Your Happiness</h1>
                        <p>{pageTitle}</p>
                    </div>
                </section>
                <section className="py-sm-5 py-4">
                    <Container>
                        <Row>
                            <Col xs={12} className="text-center">
                                <Spinner animation="border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </Spinner>
                                <p className="mt-3">{loadingText}</p>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </main>
        )
    }

    if (error) {
        return (
            <main className="encounters-page">
                <section className="encounters-hero">
                    <div className="appContainer">
                        <h1>Interact With Your Happiness</h1>
                        <p>{pageTitle}</p>
                    </div>
                </section>
                <section className="py-sm-5 py-4">
                    <Container>
                        <Row>
                            <Col xs={12}>
                                <Alert variant="danger">
                                    <Alert.Heading>{oopsHeading}</Alert.Heading>
                                    <p>{error.message || 'Failed to load users. Please try again.'}</p>
                                    <Button variant="outline-danger" onClick={handleRefresh}>
                                        {tryAgainBtn}
                                    </Button>
                                </Alert>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </main>
        )
    }
    
    if (!currentUser && users.length === 0) {
        return (
            <main className="encounters-page">
                <section className="encounters-hero">
                    <div className="appContainer">
                        <h1>Interact With Your Happiness</h1>
                        <p>{pageTitle}</p>
                    </div>
                </section>
                <section className="py-sm-5 py-4">
                    <Container>
                        <Row>
                            <Col xs={12} className="text-center">
                                <div className="interact-card-wrapper">
                                    <h3>{noProfilesHeading}</h3>
                                    <p>{noProfilesText}</p>
                                    <Button variant="primary" onClick={handleRefresh}>
                                        {refreshBtn}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </main>
        )
    }

    return (
        <main className="encounters-page">
            <section className="encounters-hero">
                <div className="appContainer">
                    <h1>Interact With Your Happiness</h1>
                    <p>{pageTitle}</p>
                </div>
            </section>
            <section className="py-4 encounters-main">
                <Container>
                    <Row>
                        <Col xs={12} className='my-3'>
                            <div className="interact-card-wrapper">
                                <div className="d-flex justify-content-end mb-3">
                                    <span className="filter-label-text">{filtersLabel}</span>
                                </div>
                                <Dropdown className='mb-3 match-filter-dropdown' autoClose={false}>
                                    <Dropdown.Toggle className='match-filter-dropdown-toggle' id="dropdown-basic">
                                        <HiAdjustmentsHorizontal />
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu align='end'>
                                        <BasicFilters
                                            onApply={handleApplyFilters}
                                            onClear={handleClearFilters}
                                            initialFilters={searchFilters}
                                        />
                                    </Dropdown.Menu>
                                </Dropdown>
                                {currentUser && (
                                    <div className="profile-card">
                                        <div className="profile-card-top">
                                            <div className="profile-distance-wrapper">
                                                <span className="profile-location fw-regular">
                                                    California ({distanceUnit === 'km' ? distanceKm : distanceMiles} {distanceUnit === 'km' ? kmAwayLabel : milesAwayLabel})
                                                </span>
                                                <select
                                                    className="distance-unit-select"
                                                    value={distanceUnit}
                                                    onChange={(e) => setDistanceUnit(e.target.value)}
                                                >
                                                    <option value="km">km</option>
                                                    <option value="mi">mi</option>
                                                </select>
                                            </div>
                                            <button className="profile-visit-btn" onClick={() => navigate(`/user/profile/${currentUser.id}`)}><FaUserAlt /></button>
                                        </div>
                                        <div className="profile-image-container">
                                            <img
                                                src={currentUser.image || '/default-profile.png'}
                                                alt={currentUser.full_name || currentUser.username}
                                                className="profile-image"
                                            />
                                            <div className="profile-info">
                                                <h4 className='mb-0 fw-medium d-flex align-items-center justify-content-center gap-1 flex-wrap'>
                                                    <span>{currentUser.full_name || currentUser.username}</span>,
                                                    {currentUser.date_of_birth &&
                                                        <span>{new Date().getFullYear() - new Date(currentUser.date_of_birth).getFullYear()}
                                                        </span>} {currentUser.email_verified_at && (
                                                            <img
                                                                src={verifiedIcon}
                                                                alt="Verified"
                                                                className="profile-verify-icon"
                                                            />
                                                        )}
                                                    <PremiumBadge user={currentUser} variant="small" />
                                                </h4>
                                            </div>
                                        </div>

                                        <div className="action-buttons p-0 pt-3">
                                            <Button
                                                variant="danger"
                                                size="lg"
                                                className="action-btn pass-btn"
                                                onClick={handlePass}
                                                disabled={passMutation.isPending}
                                            >
                                                {passMutation.isPending ? (
                                                    <Spinner size="sm" />
                                                ) : (
                                                    <FaTimes size={20} />
                                                )}
                                            </Button>
                                            <Button
                                                variant="light"
                                                size="lg"
                                                className="action-btn starred-btn"
                                                onClick={handleFavorite}
                                                disabled={favoriteMutation.isPending}
                                            >
                                                {favoriteMutation.isPending ? (
                                                    <Spinner size="sm" />
                                                ) : (
                                                    <FaStar size={20} />
                                                )}
                                            </Button>

                                            <Button
                                                variant="success"
                                                size="lg"
                                                className="action-btn like-btn"
                                                onClick={handleLike}
                                                disabled={likeMutation.isPending}
                                            >
                                                {likeMutation.isPending ? (
                                                    <Spinner size="sm" />
                                                ) : (
                                                    <FaCheck size={20} />
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {currentUserIndex >= users.length && (
                                    <div className="no-more-profiles">
                                        <h3>No more profiles to show!</h3>
                                        <p>Check back later for new matches.</p>
                                        <Button variant="primary" onClick={handleRefresh}>
                                            Refresh
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Container>
                </section>
        </main>
    )
}

export default InteractWithHappiness
